import React, { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TopSection } from '../../../ui/TopSection';
import { useLoadOrderHistory, useLoadRevenueHistory } from '../../../../utils/dataLoadingUtils';
import { useTimeControls } from '../../../../hooks/useTimeControls';
import { useFiltersStore } from '../../../../stores/appFiltersStore';

type HistoryRange = { start: Date; end: Date } | null;
const ReportHistoryRangeContext = createContext<((range: HistoryRange) => void) | null>(null);
export const useReportHistoryRange = () => useContext(ReportHistoryRangeContext);
const ReportHistoryLoader: React.FC = () => {
  useLoadOrderHistory();
  useLoadRevenueHistory();
  return null;
};

interface ReportDetailLayoutProps {
  titleKey: string;
  /** When set, `TopSection` shows the shared overall-type filter for this report. */
  overallTypeOptions?: string[];
  /** Where the back button goes. Defaults to the reports hub. */
  backTo?: string;
  children: React.ReactNode;
  /** The body loads isolated histories and publishes its own date bounds. */
  independentHistory?: boolean;
}

/**
 * Shared shell for every report detail page. Uses the same `TopSection` as the
 * dashboard / order-history pages, so the back button and the time controls
 * (top-right: day / week / month / quarter / all + custom range) are visually
 * identical across the app. Time state lives in the shared `useTimeControls`
 * store, the same one the dashboard uses, and the report bodies read the
 * selected range from it.
 */
export const ReportDetailLayout: React.FC<ReportDetailLayoutProps> = ({ titleKey, overallTypeOptions, backTo = '/reports', children, independentHistory = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Orders + revenue back the time controls (full-data range for the "all" view).
  const [historyRange, setHistoryRange] = useState<HistoryRange>(null);

  const { dateRange, setDateRange, selectedTimeOption, setSelectedTimeOption } = useTimeControls(independentHistory ? historyRange : undefined);
  const overallTypeFilter = useFiltersStore((s) => s.overallTypeFilter);
  const setOverallTypeFilter = useFiltersStore((s) => s.setOverallTypeFilter);

  return (
    <ReportHistoryRangeContext.Provider value={setHistoryRange}>
    {!independentHistory && <ReportHistoryLoader />}
    <div className="p-4 max-w-7xl mx-auto">
      <TopSection
        title={t(titleKey)}
        showMobileTitle
        backButton={true}
        handleGoBack={() => navigate(backTo)}
        dateRange={dateRange}
        setDateRange={setDateRange}
        selectedTimeOption={selectedTimeOption}
        setSelectedTimeOption={setSelectedTimeOption}
        overallTypeOptions={overallTypeOptions}
        currentOverallTypeValue={overallTypeFilter}
        onOverallTypeChange={(value) => setOverallTypeFilter(value as typeof overallTypeFilter)}
      />
      {children}
    </div>
    </ReportHistoryRangeContext.Provider>
  );
};

export default ReportDetailLayout;
